import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import HandleDeleteVeridasApp from './HandleDeleteVeridasApp';

/**
 * Key of the page the user left from, it is read back when the document loads
 * again to tell a reload of the validation apart from an ordinary navigation.
 */
const LEFT_FROM_STORAGE_KEY = 'redirectToPage';

/**
 *
 * @Author: Cecilia López Martínez
 * @JobTitle : Full Stack Developer - IBL
 * @Email : clopez@ibl.mx
 *
 * Component to handle the effect of reloading the page. The user is shown a confirmation message to verify whether they truly want to leave the page.
 * If they confirm, all registered information (INE/Passport and the selfie) is deleted. Otherwise, the information is preserved and the process continues.
 * @param model getInvitationById: Model that contains all the information of the invitation.
 *
 */
const useHandleBeforeUnload = ({ getInvitationById, validationId }) => {

    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const [isRedirected, setIsRedirected] = useState(false);

    const { handleDelete, handleDeleteStorage, mainUrl } = HandleDeleteVeridasApp({ getInvitationById , validationId });

    const handleLeave = () => {
        handleDelete?.();
        toast.warn(t("all_the_recorded_information_was_lost"));
        navigate(mainUrl);
        handleDeleteStorage();
    };

    // handleDelete and mainUrl are rebuilt on every render, the ref keeps the mount
    // effect free of them so the pending leave is only ever consumed once.
    const handleLeaveRef = useRef(handleLeave);
    handleLeaveRef.current = handleLeave;

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };

        /**
         * Remembers the page the user is leaving from.
         * pagehide is used instead of unload because unload never fires on mobile
         * browsers, and a page kept in the back/forward cache was not really left.
         *
         * @param {PageTransitionEvent} e - Browser event
         */
        const handlePageHide = (e) => {
            if (e.persisted) return;

            sessionStorage.setItem(LEFT_FROM_STORAGE_KEY, window.location.pathname);
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('pagehide', handlePageHide);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('pagehide', handlePageHide);
        };
    }, []);

    useEffect(() => {
        const leftFrom = sessionStorage.getItem(LEFT_FROM_STORAGE_KEY);

        if (!leftFrom) return;

        sessionStorage.removeItem(LEFT_FROM_STORAGE_KEY);

        // Another step of the flow left that mark behind, cancelling this validation
        // because of it would throw the user out of a process they never abandoned.
        if (leftFrom !== location.pathname) return;

        // The browser discarded the tab on its own, typically while the user was away
        // granting the camera permission, and restored it with a reload the user never asked for.
        if (document.wasDiscarded) return;

        handleLeaveRef.current();
        setIsRedirected(true);
    }, []);

    return { isRedirected, handleLeave };

};

export default useHandleBeforeUnload
